import React, { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Modal } from "antd";
import { searchEntities } from "../features/followups/services/entitySearchService";
import { logError, logInfo } from "@/shared/utils/logger.js";

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 500px;
  min-width: 0; /* Allow flexbox shrinking */
  margin: 0 1rem;

  @media (max-width: 1024px) {
    flex: 0 0 auto;
    width: auto;
    max-width: none;
    min-width: 0;
    margin: 0;
  }

  @media (min-width: 1025px) {
    min-width: 200px;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  /* Icon button replaces the bar on mobile + tablet */
  @media (max-width: 1024px) {
    display: none;
  }
`;

const SearchIconButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0.5rem;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }

  &:active {
    background: #d1d5db;
  }

  /* Show icon button on mobile + tablet */
  @media (max-width: 1024px) {
    display: flex;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent iOS zoom - must be at least 16px */
    padding: 0.375rem 2rem 0.375rem 2rem;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }
`;

const ResultsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1001;
`;

const ResultGroup = styled.div`
  padding: 0.5rem 0;
`;

const ResultGroupTitle = styled.div`
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f9fafb;
`;

const ResultItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ResultTitle = styled.div`
  font-weight: 500;
  color: #111827;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const ResultSubtitle = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const LoadingState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

// Modal-specific styles
const ModalSearchWrapper = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const ModalSearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ModalSearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ModalSearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const ModalClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }
`;

const ModalResultsContainer = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  margin-top: 1rem;
`;

const MobileSearchOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: white;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  padding-top: env(safe-area-inset-top);
`;

const MobileSearchHeader = styled.div`
  height: 4rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
`;

const MobileBackButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #111827;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.6;
  }
`;

const MobileInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
`;

const MobileSearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
  z-index: 1;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  height: 2.75rem;
  padding: 0.5rem 2.5rem 0.5rem 2.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 16px; /* Critical: prevents iOS focus-zoom */
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const MobileClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: color 0.2s;

  &:active {
    color: #374151;
  }
`;

const MobileResultsArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: white;
`;

// Hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);

    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
    } else {
      mql.addListener(onChange);
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, []);

  return isMobile;
};

const GlobalSearch = () => {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const modalSearchRef = useRef(null);
  const modalInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const searchResults = await searchEntities(query, {
          limitPerType: 5,
          maxTotal: 20,
        });
        setResults(searchResults);
        setShowResults(true);
        logInfo("Search completed", {
          query: query.trim(),
          resultCount: searchResults.length,
        });
      } catch (error) {
        logError("Search error", {
          query: query.trim(),
          error: error.message,
        });
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Lock body scroll when mobile overlay opens (focus is handled synchronously in open handler)
  useEffect(() => {
    if (!isMobileOverlayOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileOverlayOpen]);

  // Close mobile overlay if viewport changes to non-mobile
  useEffect(() => {
    if (!isMobile && isMobileOverlayOpen) {
      setIsMobileOverlayOpen(false);
    }
  }, [isMobile, isMobileOverlayOpen]);

  // Close results when clicking outside (for desktop dropdown)
  useEffect(() => {
    if (isModalOpen || isMobileOverlayOpen) return; // Don't handle outside clicks when modal/overlay is open

    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isModalOpen, isMobileOverlayOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isModalOpen && modalInputRef.current) {
      setTimeout(() => {
        modalInputRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen]);

  const handleResultClick = (result) => {
    const routeMap = {
      customer: `/customers/${result.entityId}`,
      lead: `/leads/${result.entityId}`,
      quote: `/quotes/${result.entityId}`,
      invoice: `/invoices/${result.entityId}`,
      followup: `/followups/${result.entityId}`,
      order: `/orders/${result.entityId}`,
    };

    const route = routeMap[result.entityType];
    if (route) {
      navigate(route);
      setQuery("");
      setShowResults(false);
      setIsModalOpen(false);
      setIsMobileOverlayOpen(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setShowResults(false);
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
    setShowResults(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setQuery("");
    setResults([]);
    setShowResults(false);
  };

  const handleMobileOpen = () => {
    // flushSync keeps focus inside the user-gesture call stack (required for iOS keyboard)
    flushSync(() => {
      setIsMobileOverlayOpen(true);
      setIsModalOpen(false);
      setShowResults(false);
    });
    mobileInputRef.current?.focus();
  };

  const handleIconClick = () => {
    if (isMobile) {
      handleMobileOpen();
    } else {
      handleModalOpen();
    }
  };

  const handleMobileClose = () => {
    setIsMobileOverlayOpen(false);
    setQuery("");
    setResults([]);
    setShowResults(false);
  };

  // Group results by entity type
  const groupedResults = results.reduce((acc, result) => {
    const type = result.entityType;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(result);
    return acc;
  }, {});

  const entityTypeLabels = {
    customer: "Customers",
    lead: "Leads",
    quote: "Quotes",
    invoice: "Invoices",
    followup: "Follow-ups",
    order: "Orders",
  };

  const renderResults = () => {
    if (isLoading) {
      return <LoadingState>Searching...</LoadingState>;
    }
    if (results.length === 0 && query.trim().length >= 2) {
      return <EmptyState>No results found</EmptyState>;
    }
    if (results.length === 0) {
      return null;
    }

    return (
      <>
        {Object.entries(groupedResults).map(([type, items]) => (
          <ResultGroup key={type}>
            <ResultGroupTitle>
              {entityTypeLabels[type] || type}
            </ResultGroupTitle>
            {items.map((result, index) => (
              <ResultItem
                key={`${result.entityType}-${result.entityId}-${index}`}
                onClick={() => handleResultClick(result)}
              >
                <ResultTitle>{result.displayName}</ResultTitle>
                {result.subtitle && (
                  <ResultSubtitle>{result.subtitle}</ResultSubtitle>
                )}
              </ResultItem>
            ))}
          </ResultGroup>
        ))}
      </>
    );
  };

  const shouldShowResults = showResults && (query.trim().length >= 2 || results.length > 0);
  const SearchIconSvg = (
    <svg
      width="18"
      height="18"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );

  const ClearIconSvg = (
    <svg
      width="16"
      height="16"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  return (
    <>
      <SearchContainer>
        {/* Icon button for mobile + tablet */}
        <SearchIconButton
          onClick={handleIconClick}
          type="button"
          aria-label="Search"
        >
          <svg
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </SearchIconButton>

        {/* Desktop search bar */}
        <SearchInputWrapper ref={searchRef}>
          <SearchIcon>{SearchIconSvg}</SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search customers, leads, quotes..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.trim().length >= 2 && setShowResults(true)}
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {query && (
            <ClearButton onClick={handleClear} type="button">
              {ClearIconSvg}
            </ClearButton>
          )}
        </SearchInputWrapper>

        {/* Desktop dropdown results */}
        {!isMobile &&
          shouldShowResults &&
          !isModalOpen &&
          !isMobileOverlayOpen && (
            <ResultsDropdown ref={resultsRef}>{renderResults()}</ResultsDropdown>
          )}
      </SearchContainer>

      {/* Mobile WhatsApp-style full-screen overlay */}
      {isMobileOverlayOpen && (
        <MobileSearchOverlay role="dialog" aria-modal="true">
          <MobileSearchHeader>
            <MobileBackButton
              type="button"
              onClick={handleMobileClose}
              aria-label="Close search"
            >
              <svg
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </MobileBackButton>

            <MobileInputWrapper>
              <MobileSearchIcon>
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </MobileSearchIcon>

              <MobileSearchInput
                ref={mobileInputRef}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => query.trim().length >= 2 && setShowResults(true)}
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />

              {query && (
                <MobileClearButton
                  type="button"
                  onClick={handleClear}
                  aria-label="Clear search"
                >
                  {ClearIconSvg}
                </MobileClearButton>
              )}
            </MobileInputWrapper>
          </MobileSearchHeader>

          <MobileResultsArea>
            {shouldShowResults && renderResults()}
          </MobileResultsArea>
        </MobileSearchOverlay>
      )}

      {/* Modal for tablet search */}
      <Modal
        title="Search"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnHidden
        maskClosable
      >
        <ModalSearchWrapper ref={modalSearchRef}>
          <ModalSearchInputWrapper>
            <ModalSearchIcon>
              <svg
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </ModalSearchIcon>
            <ModalSearchInput
              ref={modalInputRef}
              type="text"
              placeholder="Search customers, leads, quotes..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => query.trim().length >= 2 && setShowResults(true)}
            />
            {query && (
              <ModalClearButton onClick={handleClear} type="button">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </ModalClearButton>
            )}
          </ModalSearchInputWrapper>

          {/* Modal results */}
          {shouldShowResults && (
            <ModalResultsContainer>{renderResults()}</ModalResultsContainer>
          )}
        </ModalSearchWrapper>
      </Modal>
    </>
  );
};

export default GlobalSearch;

